import { useCallback, useState } from "react";
import { callApi } from "@/utils/http-utils";

type AnyRow = Record<string, any>;

const toBool = (v: any) =>
  v === true ||
  v === 1 ||
  v === "1" ||
  v === "true" ||
  v === "TRUE" ||
  v === "t" ||
  v === "T";

export function useClusterMapping(rows: AnyRow[] = []) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  const runOneTime = useCallback(
    async (promptText: string) => {
      setSending(true);
      setError("");
      try {
        // 1) only active modalities
        const active = (rows ?? []).filter((m) =>
          toBool(m.isActive ?? m.status ?? m.is_active),
        );
        if (!active.length) {
          setError("No active modalities to process.");
          return false;
        }

        // 2) build context
        const context = {
          modalities: active.map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description ?? "",
          })),
        };

        // 3) call LLM one-time endpoint
        const res = await callApi("data-api/one-time", {
          method: "POST",
          body: { contextString: JSON.stringify(context), prompt: promptText },
          accept: "application/json",
        });
        const text = await res.text();

        // 4) parse JSON
        let arr: Array<any> = [];
        try {
          arr = JSON.parse(text);
        } catch {
          throw new Error("Model did not return JSON.");
        }

        // 5) normalize to [{ id, primaryCluster }]
        const results = arr
          .map((x) => ({
            id: Number(x.id ?? x.ID),
            primaryCluster: String(
              x.PrimaryCluster ?? x.primaryCluster ?? "",
            ).trim(),
          }))
          .filter((r) => Number.isFinite(r.id) && r.primaryCluster);

        if (!results.length) {
          setError("No mappings returned.");
          return false;
        }

        // 6) apply mappings
        const applyRes = await callApi(
          "data-api/modality-cluster-mapping-runs",
          {
            method: "POST",
            body: { results },
            accept: "application/json",
          },
        );
        if (!applyRes.ok) {
          throw new Error(
            `Apply failed: ${applyRes.status} ${await applyRes.text()}`,
          );
        }

        setSuccess(true);
        return true;
      } catch (e: any) {
        const msg = e?.message || "Failed to run mapping.";
        setError(msg);
        return false;
      } finally {
        setSending(false);
      }
    },
    [rows],
  );

  return { sending, error, success, setSuccess, runOneTime };
}
